import { useTicketHooks } from "@/features/tickets/hooks/ticket-hooks";
import { useCustomTemplates } from "@/lib/hooks/settings/use-custom-templates";
import { MSP_TASK_TEMPLATES, TaskTemplate } from "@/lib/types/task-templates";
import { TIME_ENTRY_TEMPLATES } from "@/features/time-entries/components/constants/time-entry-templates";
import { BaseTemplate } from "@/components/shared/UniversalEntityPage/types";
import { TicketTemplate } from "@/lib/types/database";
import { ensureTicketTemplateBaseCompatibility } from "@/lib/utils/crm/template-helpers";

export type TemplateType = "ticket" | "task" | "time-entry" | "custom";

export interface GetTemplatesOptions {
  templateType: TemplateType;
  ticketTemplates?: TicketTemplate[];
  userCustomTemplates?: BaseTemplate[];
  customTemplates?: BaseTemplate[];
}

/**
 * Get templates based on template type
 *
 * Extracted from ReusableTemplateSelector to separate concerns and improve maintainability.
 */
export function getTemplates({
  templateType,
  ticketTemplates = [],
  userCustomTemplates = [],
  customTemplates = [],
}: GetTemplatesOptions): BaseTemplate[] {
  switch (templateType) {
    case "ticket":
      return (Array.isArray(ticketTemplates) ? ticketTemplates : []).map((t: TicketTemplate) =>
        ensureTicketTemplateBaseCompatibility(t)
      );

    case "task":
      // TaskTemplate already extends BaseTemplate, so we can use them directly
      const predefinedTaskTemplates: BaseTemplate[] = MSP_TASK_TEMPLATES.flatMap(
        (category) => category.templates
      );

      const customTaskTemplates: BaseTemplate[] = (
        Array.isArray(userCustomTemplates) ? userCustomTemplates : []
      ).map((template) => ({
        ...template,
        name: `${template.name} (Custom)`,
      }));

      return [...predefinedTaskTemplates, ...customTaskTemplates];

    case "time-entry":
      // Map TimeEntryTemplate to BaseTemplate
      return TIME_ENTRY_TEMPLATES.map((template) => ({
        id: template.id,
        name: template.name,
        description: template.description,
        category: template.category,
        title: template.name, // Use name as title
        description_text: template.fields.description,
        tags: template.fields.tags,
      }));

    case "custom":
      return Array.isArray(customTemplates) ? customTemplates : [];

    default:
      return [];
  }
}
